import { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { Car, ArrowLeft, Calendar as CalendarIcon, Clock, MapPin, Check, Loader2, Home, Building2 } from "lucide-react";
import axios from "axios";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Header = () => (
  <header className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-xl border-b border-zinc-800">
    <div className="container-custom">
      <div className="flex items-center justify-between h-20">
        <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
          <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center">
            <Car className="w-6 h-6 text-black" />
          </div>
          <span className="text-xl font-medium tracking-tight text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Esthétique Auto TR
          </span>
        </Link>
        <Link to="/">
          <Button variant="ghost" className="text-zinc-400 hover:text-white" data-testid="back-home-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour
          </Button>
        </Link>
      </div>
    </div>
  </header>
);

const BookingPage = () => {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  
  const [services, setServices] = useState([]);
  const [dateInfo, setDateInfo] = useState(null);
  const [bookedDates, setBookedDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  const [selectedService, setSelectedService] = useState(serviceId || "");
  const [selectedDate, setSelectedDate] = useState(null);
  const [locationType, setLocationType] = useState("domicile");
  const [selectedExtras, setSelectedExtras] = useState([]);
  
  const [formData, setFormData] = useState({
    client_name: "",
    client_email: "",
    client_phone: "",
    address: "",
    vehicle_brand: "",
    vehicle_model: "",
    notes: ""
  });

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const [servicesRes, bookedRes] = await Promise.all([
          axios.get(`${API}/services`),
          axios.get(`${API}/booked-dates`)
        ]);
        setServices(servicesRes.data);
        setBookedDates(bookedRes.data.booked_dates || []);
        if (serviceId) {
          setSelectedService(serviceId);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Erreur lors du chargement des services");
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, [serviceId]);

  useEffect(() => {
    const fetchDateInfo = async () => {
      if (!selectedDate) return;
      
      try {
        const dateStr = format(selectedDate, "yyyy-MM-dd");
        const response = await axios.get(`${API}/timeslots?date=${dateStr}`);
        setDateInfo(response.data);
      } catch (error) {
        console.error("Error fetching date info:", error);
        toast.error("Erreur lors de la vérification de la date");
      }
    };
    fetchDateInfo();
  }, [selectedDate]);

  // Reset extras when service changes
  useEffect(() => {
    setSelectedExtras([]);
  }, [selectedService]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleExtraToggle = (extraId) => {
    setSelectedExtras(prev => 
      prev.includes(extraId) 
        ? prev.filter(id => id !== extraId)
        : [...prev, extraId]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedService || !selectedDate) {
      toast.error("Veuillez sélectionner un service et une date");
      return;
    }

    if (!dateInfo?.available) {
      toast.error("Cette date n'est pas disponible");
      return;
    }

    if (!formData.client_name || !formData.client_email || !formData.client_phone) {
      toast.error("Veuillez remplir vos coordonnées");
      return;
    }

    if (locationType === "domicile" && !formData.address) {
      toast.error("Veuillez indiquer votre adresse pour le service à domicile");
      return;
    }

    setSubmitting(true);

    const service = services.find(s => s.id === selectedService);
    const extraNames = selectedExtras.map(extraId => {
      const extra = service?.extras?.find(e => e.id === extraId);
      return extra?.name;
    }).filter(Boolean);
    
    const appointmentData = {
      ...formData,
      service_id: selectedService,
      service_name: service?.name || "",
      date: format(selectedDate, "yyyy-MM-dd"),
      time_slot: dateInfo?.is_weekend ? "Journée complète" : "À partir de 16h",
      home_service: locationType === "domicile",
      location_type: locationType,
      selected_extras: extraNames
    };

    try {
      const response = await axios.post(`${API}/appointments`, appointmentData);
      toast.success("Réservation confirmée !");
      navigate(`/confirmation/${response.data.id}`);
    } catch (error) {
      console.error("Error creating appointment:", error);
      toast.error("Erreur lors de la réservation. Veuillez réessayer.");
    } finally {
      setSubmitting(false);
    }
  };

  const selectedServiceData = services.find(s => s.id === selectedService);

  const disabledDays = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Disable past dates
    if (date < today) return true;
    
    // Disable already booked dates
    const dateStr = format(date, "yyyy-MM-dd");
    if (bookedDates.includes(dateStr)) return true;
    
    return false;
  };

  // Calculate total price
  const calculateTotal = () => {
    if (!selectedServiceData) return 0;
    let total = selectedServiceData.price || 0;
    if (selectedServiceData.extras) {
      selectedExtras.forEach(extraId => {
        const extra = selectedServiceData.extras.find(e => e.id === extraId);
        if (extra) total += extra.price;
      });
    }
    return total;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-white animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black" data-testid="booking-page">
      <Header />
      
      <main className="pt-32 pb-16">
        <div className="container-custom">
          <div className="text-center mb-12">
            <span className="text-xs tracking-[0.3em] uppercase text-zinc-500 mb-4 inline-block">Réservation</span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-medium text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              Prendre Rendez-vous
            </h1>
            <p className="text-zinc-500 mt-4 text-sm">
              Fins de semaine : Toute la journée | Semaine : À partir de 16h
            </p>
          </div>

          <form onSubmit={handleSubmit} className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              
              {/* Left Column - Service & Date Selection */}
              <div className="space-y-6">
                
                {/* Service Selection */}
                <Card className="bg-zinc-900 border-zinc-800">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                      <Car className="w-5 h-5 text-white" />
                      Choisir un Service
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Select value={selectedService} onValueChange={setSelectedService}>
                      <SelectTrigger 
                        className="bg-black border-zinc-800 text-white focus:border-white focus:ring-1 focus:ring-white"
                        data-testid="service-select"
                      >
                        <SelectValue placeholder="Sélectionner un service" />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800">
                        {services.map(service => (
                          <SelectItem 
                            key={service.id} 
                            value={service.id}
                            className="text-white hover:bg-zinc-800 focus:bg-zinc-800"
                            data-testid={`service-option-${service.id}`}
                          >
                            {service.name} - {service.price_text || `${service.price} $`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {selectedServiceData && (
                      <div className="mt-4 p-4 rounded-lg bg-black border border-zinc-800">
                        <p className="text-sm text-zinc-400 mb-3">{selectedServiceData.description}</p>
                        {selectedServiceData.includes && (
                          <div className="mb-3">
                            <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Inclus :</p>
                            <ul className="space-y-1">
                              {selectedServiceData.includes.map((item, i) => (
                                <li key={i} className="flex items-center gap-2 text-xs text-zinc-400">
                                  <Check className="w-3 h-3 text-green-500" />
                                  {item}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {selectedServiceData.note && (
                          <p className="text-xs text-zinc-500 italic border-t border-zinc-800 pt-3 mt-3">
                            "{selectedServiceData.note}"
                          </p>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Extras Selection (for Remise à Neuf) */}
                {selectedServiceData?.extras && selectedServiceData.extras.length > 0 && (
                  <Card className="bg-zinc-900 border-zinc-800">
                    <CardHeader>
                      <CardTitle className="text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                        Options Supplémentaires
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {selectedServiceData.extras.map(extra => (
                        <div 
                          key={extra.id}
                          className={`p-4 rounded-lg border cursor-pointer transition-all duration-300 ${
                            selectedExtras.includes(extra.id) 
                              ? 'bg-white/10 border-white' 
                              : 'bg-black border-zinc-800 hover:border-zinc-700'
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleExtraToggle(extra.id);
                          }}
                          data-testid={`extra-${extra.id}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div 
                                className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                                  selectedExtras.includes(extra.id) 
                                    ? 'bg-white border-white' 
                                    : 'border-zinc-600'
                                }`}
                              >
                                {selectedExtras.includes(extra.id) && (
                                  <Check className="w-3 h-3 text-black" />
                                )}
                              </div>
                              <div>
                                <p className="text-white font-medium text-sm">{extra.name}</p>
                                {extra.description && (
                                  <p className="text-xs text-zinc-500">{extra.description}</p>
                                )}
                              </div>
                            </div>
                            <span className="text-white font-bold">+{extra.price} $</span>
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}

                {/* Date Selection */}
                <Card className="bg-zinc-900 border-zinc-800">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                      <CalendarIcon className="w-5 h-5 text-white" />
                      Choisir une Date
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="flex justify-center">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={setSelectedDate}
                      disabled={disabledDays}
                      locale={fr}
                      className="rounded-md border border-zinc-800 bg-black text-white"
                      data-testid="booking-calendar"
                    />
                  </CardContent>
                </Card>

                {/* Date Info */}
                {selectedDate && dateInfo && (
                  <Card className={`border ${dateInfo.available ? 'bg-zinc-900 border-zinc-800' : 'bg-red-900/20 border-red-800'}`}>
                    <CardContent className="pt-6">
                      <div className="flex items-start gap-4">
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${dateInfo.available ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                          {dateInfo.available ? (
                            <Check className="w-6 h-6 text-green-500" />
                          ) : (
                            <Clock className="w-6 h-6 text-red-500" />
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="text-white font-medium">
                            {dateInfo.available ? "Date disponible" : "Date non disponible"}
                          </p>
                          <p className="text-sm text-zinc-400 mt-1">
                            {dateInfo.message}
                          </p>
                          {!dateInfo.is_weekend && dateInfo.available && (
                            <div className="mt-3 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                              <p className="text-xs text-yellow-500">
                                <strong>Note :</strong> En semaine, selon le service choisi, le travail pourrait s'étendre sur plusieurs jours.
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Right Column - Client Info */}
              <div className="space-y-6">
                
                {/* Contact Info */}
                <Card className="bg-zinc-900 border-zinc-800">
                  <CardHeader>
                    <CardTitle className="text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                      Vos Coordonnées
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label htmlFor="client_name" className="text-zinc-400 text-sm">Nom complet *</Label>
                      <Input
                        id="client_name"
                        name="client_name"
                        value={formData.client_name}
                        onChange={handleInputChange}
                        className="bg-black border-zinc-800 text-white focus:border-white focus:ring-1 focus:ring-white mt-1"
                        placeholder="Jean Dupont"
                        required
                        data-testid="input-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="client_email" className="text-zinc-400 text-sm">Courriel *</Label>
                      <Input
                        id="client_email"
                        name="client_email"
                        type="email"
                        value={formData.client_email}
                        onChange={handleInputChange}
                        className="bg-black border-zinc-800 text-white focus:border-white focus:ring-1 focus:ring-white mt-1"
                        placeholder="jean@example.com"
                        required
                        data-testid="input-email"
                      />
                    </div>
                    <div>
                      <Label htmlFor="client_phone" className="text-zinc-400 text-sm">Téléphone *</Label>
                      <Input
                        id="client_phone"
                        name="client_phone"
                        type="tel"
                        value={formData.client_phone}
                        onChange={handleInputChange}
                        className="bg-black border-zinc-800 text-white focus:border-white focus:ring-1 focus:ring-white mt-1"
                        placeholder="514 123-4567"
                        required
                        data-testid="input-phone"
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Location Type */}
                <Card className="bg-zinc-900 border-zinc-800">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                      <MapPin className="w-5 h-5 text-white" />
                      Lieu du Service
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <RadioGroup value={locationType} onValueChange={setLocationType} className="space-y-3">
                      <div 
                        className={`flex items-center space-x-3 p-4 rounded-lg border cursor-pointer transition-all ${
                          locationType === 'domicile' ? 'border-white bg-white/10' : 'border-zinc-800 hover:border-zinc-700'
                        }`}
                        onClick={() => setLocationType('domicile')}
                      >
                        <RadioGroupItem value="domicile" id="domicile" className="border-zinc-600 text-white" />
                        <Label htmlFor="domicile" className="flex items-center gap-2 cursor-pointer flex-1">
                          <Home className="w-5 h-5 text-white" />
                          <div>
                            <p className="text-white font-medium">Service à domicile</p>
                            <p className="text-xs text-zinc-500">Je me déplace chez vous</p>
                          </div>
                        </Label>
                      </div>
                      <div 
                        className={`flex items-center space-x-3 p-4 rounded-lg border cursor-pointer transition-all ${
                          locationType === 'garage' ? 'border-white bg-white/10' : 'border-zinc-800 hover:border-zinc-700'
                        }`}
                        onClick={() => setLocationType('garage')}
                      >
                        <RadioGroupItem value="garage" id="garage" className="border-zinc-600 text-white" />
                        <Label htmlFor="garage" className="flex items-center gap-2 cursor-pointer flex-1">
                          <Building2 className="w-5 h-5 text-white" />
                          <div>
                            <p className="text-white font-medium">En garage</p>
                            <p className="text-xs text-zinc-500">Garage avec échangeur d'air (idéal pour céramique)</p>
                          </div>
                        </Label>
                      </div>
                    </RadioGroup>

                    {locationType === "domicile" && (
                      <div className="mt-4">
                        <Label htmlFor="address" className="text-zinc-400 text-sm">Adresse complète *</Label>
                        <Textarea
                          id="address"
                          name="address"
                          value={formData.address}
                          onChange={handleInputChange}
                          className="bg-black border-zinc-800 text-white focus:border-white focus:ring-1 focus:ring-white mt-1"
                          placeholder="123 Rue Principale, Montréal, QC"
                          rows={2}
                          data-testid="input-address"
                        />
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Vehicle Info */}
                <Card className="bg-zinc-900 border-zinc-800">
                  <CardHeader>
                    <CardTitle className="text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                      Votre Véhicule
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="vehicle_brand" className="text-zinc-400 text-sm">Marque</Label>
                        <Input
                          id="vehicle_brand"
                          name="vehicle_brand"
                          value={formData.vehicle_brand}
                          onChange={handleInputChange}
                          className="bg-black border-zinc-800 text-white focus:border-white focus:ring-1 focus:ring-white mt-1"
                          placeholder="Porsche"
                          data-testid="input-vehicle-brand"
                        />
                      </div>
                      <div>
                        <Label htmlFor="vehicle_model" className="text-zinc-400 text-sm">Modèle</Label>
                        <Input
                          id="vehicle_model"
                          name="vehicle_model"
                          value={formData.vehicle_model}
                          onChange={handleInputChange}
                          className="bg-black border-zinc-800 text-white focus:border-white focus:ring-1 focus:ring-white mt-1"
                          placeholder="911 Carrera"
                          data-testid="input-vehicle-model"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="notes" className="text-zinc-400 text-sm">Notes / Demandes particulières</Label>
                      <Textarea
                        id="notes"
                        name="notes"
                        value={formData.notes}
                        onChange={handleInputChange}
                        className="bg-black border-zinc-800 text-white focus:border-white focus:ring-1 focus:ring-white mt-1"
                        placeholder="Informations supplémentaires..."
                        rows={3}
                        data-testid="input-notes"
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Price Summary */}
                {selectedServiceData && (
                  <Card className="bg-zinc-900 border-zinc-800">
                    <CardContent className="pt-6">
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-zinc-400">{selectedServiceData.name}</span>
                          <span className="text-white">{selectedServiceData.price_text || `${selectedServiceData.price} $`}</span>
                        </div>
                        {selectedExtras.map(extraId => {
                          const extra = selectedServiceData.extras?.find(e => e.id === extraId);
                          if (!extra) return null;
                          return (
                            <div key={extraId} className="flex justify-between text-sm">
                              <span className="text-zinc-400">+ {extra.name}</span>
                              <span className="text-white">{extra.price} $</span>
                            </div>
                          );
                        })}
                        <div className="flex justify-between text-lg font-bold pt-3 border-t border-zinc-800 mt-3">
                          <span className="text-white">Total estimé</span>
                          <span className="text-white">{calculateTotal()} $</span>
                        </div>
                        {selectedServiceData.id === 'service-3' && (
                          <p className="text-xs text-zinc-500 italic">* Prix final à confirmer selon vos besoins</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={submitting || !selectedService || !selectedDate || !dateInfo?.available}
                  className="w-full bg-white text-black hover:bg-zinc-200 font-medium py-6 text-base disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="submit-booking-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Réservation en cours...
                    </>
                  ) : (
                    <>
                      <Check className="w-5 h-5 mr-2" />
                      Confirmer la Réservation
                    </>
                  )}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default BookingPage;
